import { useEffect, useState } from "react";
import { Sparkles, Trophy, Star } from "lucide-react";

import { useProfileSwitcherStore } from "@/stores/profile-switcher-store";

type ProgressionEvent =
  | { type: "progression.xp"; profileId: string; amount: number; newXp: number }
  | { type: "progression.levelup"; profileId: string; level: number; title: string }
  | { type: "progression.achievement"; profileId: string; id: string; name: string; xp: number };

interface Toast {
  key: number;
  kind: "xp" | "levelup" | "achievement";
  title: string;
  sub: string;
}

let seq = 0;

/** Listens for realtime progression events and shows transient toasts. Mount once. */
export function XpToasts() {
  const activeProfileId = useProfileSwitcherStore((s) => s.activeProfileId);
  const [toasts, setToasts] = useState<Toast[]>([]);

  useEffect(() => {
    const onEvent = (e: Event) => {
      const detail = (e as CustomEvent<ProgressionEvent>).detail;
      if (!detail) return;
      // Only toast for the active profile (or the default account-profile when none is set).
      if (activeProfileId && detail.profileId && detail.profileId !== activeProfileId) return;

      let toast: Toast | null = null;
      if (detail.type === "progression.xp") {
        toast = { key: ++seq, kind: "xp", title: `+${detail.amount} XP`, sub: "Nice play" };
      } else if (detail.type === "progression.levelup") {
        toast = { key: ++seq, kind: "levelup", title: `Level ${detail.level}`, sub: detail.title };
      } else if (detail.type === "progression.achievement") {
        toast = { key: ++seq, kind: "achievement", title: detail.name, sub: `Achievement · +${detail.xp} XP` };
      }
      if (!toast) return;
      const t = toast;
      setToasts((prev) => [...prev, t].slice(-4));
      setTimeout(() => setToasts((prev) => prev.filter((x) => x.key !== t.key)), 4200);
    };
    window.addEventListener("slumbr:progression", onEvent as EventListener);
    return () => window.removeEventListener("slumbr:progression", onEvent as EventListener);
  }, [activeProfileId]);

  if (toasts.length === 0) return null;

  return (
    <div className="pointer-events-none fixed bottom-5 right-5 z-[200] flex flex-col gap-2">
      {toasts.map((t) => (
        <div
          key={t.key}
          className="flex items-center gap-3 rounded-2xl border border-white/10 bg-[#12131a]/95 px-4 py-3 shadow-[0_8px_30px_-8px_rgba(0,0,0,0.7)] backdrop-blur animate-[slideIn_0.25s_ease-out]"
          style={{ animation: "slumbrXpIn .28s cubic-bezier(.2,.9,.3,1.2)" }}
        >
          <div
            className={
              "flex h-9 w-9 shrink-0 items-center justify-center rounded-xl " +
              (t.kind === "levelup"
                ? "bg-gradient-to-br from-[#ffd60a] to-[#f59e0b] text-[#1a1500]"
                : t.kind === "achievement"
                  ? "bg-gradient-to-br from-[#a78bfa] to-[#6d28d9] text-white"
                  : "bg-gradient-to-br from-[#22d3ee] to-[#0891b2] text-[#06121f]")
            }
          >
            {t.kind === "levelup" ? <Star className="h-5 w-5" /> : t.kind === "achievement" ? <Trophy className="h-5 w-5" /> : <Sparkles className="h-5 w-5" />}
          </div>
          <div className="min-w-0">
            <p className="truncate text-sm font-black text-white">{t.title}</p>
            <p className="truncate text-xs text-[#8b93c7]">{t.sub}</p>
          </div>
        </div>
      ))}
      <style>{`@keyframes slumbrXpIn{from{opacity:0;transform:translateX(24px) scale(.96)}to{opacity:1;transform:none}}`}</style>
    </div>
  );
}
